/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable unicorn/no-null */
import logger from 'logger';
import { MissionLo } from 'pages/MissionSelection/components/MissionList';
import { Stage } from 'services/api';
import { getSessionItem, removeSessionItem, setSessionItem } from 'utils/helper';
import { StateCreator } from 'zustand';

export type Answer = string[] | { id: string; text?: string; image?: string }[];

export type QuestionStatus = {
  status: string;
  index: number;
  answer: Answer;
};

export type Student = {
  grade: string;
  name: string;
  id: string;
};

export interface AppState {
  loaderCount: number;
  questionStatusArray: QuestionStatus[] | null;
  isInternetIssue: boolean;
  currentLevel: string | null;
  currentMission: MissionLo | undefined;
  currentStage: Stage | undefined;
  serverTime?: string;
  performanceReference?: number;
  currentStudent: Student | undefined;
  performanceTimeoriginReference?: number;
  logoutSource?: string;
  showCompletedLevels: boolean;
  moduleCompletionThreshold: number | undefined;

  setShowLoader: (showLoader: boolean) => void;
  setQuestionStatusArray: (questionStatusArray: QuestionStatus[] | null) => void;
  setInternetIssue: (isInternetIssue: boolean) => void;
  setCurrentLevel: (level: string) => void;
  setCurrentMission: (mission: MissionLo | undefined) => void;
  setCurrentStage: (stage: Stage | undefined) => void;
  setServerTime: (serverTime: string) => void;
  getCurrentTime: () => number;
  loginStudent: (student: Student, sessionId: string) => void;
  logoutStudent: (extra: { source?: string }) => void;
  setShowCompletedLevels: (showCompletedLevels: boolean) => void;
  setModuleCompletionThreshold: (moduleCompletionThreshold: number) => void;
}
const getCurrentStudent = () => {
  let studentData;
  const studentId = getSessionItem('studentId');
  const sessionId = getSessionItem('sessionId');
  if (studentId && sessionId) {
    studentData = getSessionItem('currentStudent') as Student;
  }
  return studentData;
};

export const createAppSlice: StateCreator<AppState, [['zustand/devtools', never]]> = (
  set,
  get,
) => ({
  loaderCount: 0,
  logoutSource: undefined,
  questionStatusArray: null,
  isInternetIssue: false,
  currentLevel: null,
  currentMission: undefined,
  currentStage: undefined,
  serverTime: undefined,
  performanceTimeReference: undefined,
  currentStudent: getCurrentStudent(),
  performanceTimeoriginReference: undefined,
  showCompletedLevels: true,
  moduleCompletionThreshold: undefined,

  setShowLoader: (showLoader) =>
    set(
      (state) => ({
        loaderCount: showLoader ? state.loaderCount + 1 : state.loaderCount - 1,
      }),
      false,
      'appSlice/setShowLoader',
    ),

  setQuestionStatusArray: (questionStatusArray) =>
    set({ questionStatusArray }, false, 'appSlice/setQuestionStatusArray'),

  setInternetIssue: (isInternetIssue) =>
    set({ isInternetIssue }, false, 'appSlice/setInternetIssue'),

  setCurrentLevel: (level) => set({ currentLevel: level }, false, 'appSlice/setCurrentLevel'),

  setCurrentMission: (mission) =>
    set({ currentMission: mission }, false, 'appSlice/setCurrentMission'),

  setCurrentStage: (stage) => set({ currentStage: stage }, false, 'appSlice/setCurrentStage'),
  setServerTime: (serverTime) => {
    const { getCurrentTime } = get();
    const currentTime = getCurrentTime();
    set(
      {
        serverTime,
        performanceReference: performance.now(),
        performanceTimeoriginReference: performance.timeOrigin,
      },
      false,
      'appSlice/setServerTime',
    );
    const newTime = getCurrentTime();
    const diff = newTime - currentTime;
    if (Math.abs(diff) > 60 * 1000) {
      logger.warn('Time difference is too big', diff);
    }
  },
  getCurrentTime: () => {
    const { serverTime, performanceReference, performanceTimeoriginReference } = get();
    const result =
      serverTime && performanceReference && performanceTimeoriginReference
        ? new Date(serverTime).getTime() +
          Math.floor(performance.now() - performanceReference) +
          Math.floor(performance.timeOrigin - performanceTimeoriginReference)
        : Date.now();

    // store.setState((state) => state, false, `appSlice/${result}`);
    return result;
  },
  loginStudent: (student, sessionId) => {
    set({ currentStudent: student, logoutSource: undefined }, false, 'appSlice/loginStudent');
    setSessionItem('currentStudent', student);
    setSessionItem('studentId', student.id);
    setSessionItem('sessionId', sessionId);
  },

  logoutStudent: ({ source }) => {
    set({ currentStudent: undefined, logoutSource: source }, false, 'appSlice/setCurrentStudent');
    removeSessionItem('currentStudent');
    removeSessionItem('studentId');
    removeSessionItem('sessionId');
  },
  setShowCompletedLevels: (showCompletedLevels) =>
    set({ showCompletedLevels }, false, 'appSlice/showCompletedLevels'),

  setModuleCompletionThreshold: (moduleCompletionThreshold) =>
    set({ moduleCompletionThreshold }, false, 'appSlice/setModuleCompletionThreshold'),
});
